import { useEffect, useRef, useState } from "react";
import { Button } from "../../../components/ui/Button";
import { Field, TextInput } from "../../../components/ui/Field";
import { Select } from "../../../components/ui/Select";
import { api } from "../../../lib/api/endpoints";
import type { DocLinkHit } from "../../../lib/api/models";
import { PdfPanel } from "../../document/PdfPanel";

type Request = { n: number; documentId?: string; layoutKey: string; label: string };

export function PreviewPane({
  themeId,
  layoutKeys,
  beforeRender,
}: {
  themeId: string;
  layoutKeys: string[];
  beforeRender: () => Promise<boolean>;
}) {
  const [layoutKey, setLayoutKey] = useState(layoutKeys[0] ?? "technical");
  const [doc, setDoc] = useState<DocLinkHit | null>(null);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<DocLinkHit[]>([]);
  const [req, setReq] = useState<Request | null>(null);
  // Late search answers (and answers after unmount) are ignored.
  const searchSeq = useRef(0);
  useEffect(
    () => () => {
      searchSeq.current += 1;
    },
    [],
  );

  const render = async () => {
    if (!(await beforeRender())) return;
    setReq((r) => ({
      n: (r?.n ?? 0) + 1,
      documentId: doc?.id,
      layoutKey,
      label: doc ? doc.title : "the sample document",
    }));
  };

  return (
    <section
      aria-label="Preview"
      className="flex min-h-[70vh] flex-col gap-3 rounded-xl border border-line bg-surface p-5"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Layout">
          {(id) => (
            <Select
              id={id}
              value={layoutKey}
              onChange={setLayoutKey}
              options={layoutKeys.map((k) => ({ value: k, label: k }))}
            />
          )}
        </Field>
        <Field label="Document" hint={doc ? `Using ${doc.code}` : "Empty: the sample document with every block type."}>
          {(id, describedBy) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              placeholder="Search by code or title"
              value={q}
              onChange={(e) => {
                const value = e.target.value;
                const mine = ++searchSeq.current;
                setQ(value);
                setDoc(null);
                if (value.trim().length < 2) {
                  setHits([]);
                  return;
                }
                api.docLinks(value.trim()).then(
                  (found) => {
                    if (mine === searchSeq.current) setHits(found);
                  },
                  () => {
                    if (mine === searchSeq.current) setHits([]);
                  },
                );
              }}
            />
          )}
        </Field>
      </div>
      {hits.length > 0 && !doc ? (
        <ul className="grid gap-1 text-sm">
          {hits.slice(0, 6).map((h) => (
            <li key={h.id}>
              <button
                type="button"
                className="w-full rounded px-2 py-1 text-left hover:bg-green-wash"
                onClick={() => {
                  setDoc(h);
                  setQ(h.code);
                  setHits([]);
                }}
              >
                <span className="kd-codetag mr-2">{h.code}</span>
                {h.title}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div>
        <Button onClick={() => void render()}>Render preview</Button>
      </div>
      {req ? (
        <div className="min-h-0 flex-1">
          <PdfPanel
            key={req.n}
            title={req.label}
            load={() =>
              api.themes.previewDraft(
                themeId,
                req.documentId
                  ? { documentId: req.documentId, layoutKey: req.layoutKey }
                  : { layoutKey: req.layoutKey },
              )
            }
          />
        </div>
      ) : (
        <p className="text-sm text-ink-3">The preview uses the real PDF pipeline, the same one exports use.</p>
      )}
    </section>
  );
}
